import { forwardRef } from 'react';

export const EMOJI_COLOR_CLASSES = [
  'bg-emoji-1',
  'bg-emoji-2',
  'bg-emoji-3',
  'bg-emoji-4',
  'bg-emoji-5',
  'bg-emoji-6',
  'bg-emoji-7',
] as const;

export const DEFAULT_EMOJIS = [
  '🦄',
  '🐶',
  '🐱',
  '🦊',
  '🐸',
  '🐵',
  '🐰',
  '🦁',
  '🐼',
  '🐨',
  '🐯',
  '🦋',
  '🌺',
  '🌟',
  '🎨',
  '🎵',
  '🚀',
  '⚽',
  '🏀',
  '📚',
  '🎮',
  '🧩',
  '🎪',
  '🌈',
  '🍕',
  '🧸',
  '🎭',
  '🌻',
] as const;

const EMOJI_COLOR_LABELS: Record<string, string> = {
  'bg-emoji-1': 'Lavender',
  'bg-emoji-2': 'Cream',
  'bg-emoji-3': 'Light blue',
  'bg-emoji-4': 'Pink',
  'bg-emoji-5': 'Pale yellow',
  'bg-emoji-6': 'Mint',
  'bg-emoji-7': 'Periwinkle',
};

export interface EmojiPickerProps {
  selectedEmoji: string;
  selectedColor: string;
  onEmojiChange: (emoji: string) => void;
  onColorChange: (colorClass: string) => void;
  emojis?: readonly string[];
  className?: string;
}

export const EmojiPicker = forwardRef<HTMLDivElement, EmojiPickerProps>(
  (
    {
      selectedEmoji,
      selectedColor,
      onEmojiChange,
      onColorChange,
      emojis = DEFAULT_EMOJIS,
      className = '',
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={['flex flex-col gap-6', className].filter(Boolean).join(' ')}
      >
        {/* Emoji grid section */}
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium text-foreground">Emojis</span>
          {/* The column floor matches the 44px button. It was 40px, so every
              cell was 4px narrower than what it held and each row spilled
              past the screen's 20px gutter — the "grids run edge to edge"
              QA filed on YAR-143 / YAR-145. */}
          <div
            className="grid gap-2 justify-items-center grid-cols-[repeat(auto-fill,minmax(44px,1fr))]"
            role="radiogroup"
            aria-label="Select an emoji"
          >
            {emojis.map((emoji) => {
              const isSelected = emoji === selectedEmoji;
              const buttonClasses = [
                'w-11 h-11 rounded-sm flex items-center justify-center text-xl cursor-pointer',
                'border transition-all duration-150',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                isSelected
                  ? 'border-primary ring-2 ring-primary ring-offset-1 bg-background'
                  : 'border-border bg-background hover:border-border-strong',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <button
                  key={emoji}
                  type="button"
                  className={buttonClasses}
                  onClick={() => onEmojiChange(emoji)}
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={emoji}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
        </div>

        {/* Color swatches section */}
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium text-foreground">Colors</span>
          {/* `flex-wrap`: seven 44px swatches plus gaps are 356px, wider than
              a 375px phone's content box, so the single row overflowed the
              gutter. Wrapping is also what the story asks for (AC6 of
              YAR-143: the swatches sit across two rows). */}
          <div
            className="flex flex-wrap gap-2"
            role="radiogroup"
            aria-label="Select a background color"
          >
            {EMOJI_COLOR_CLASSES.map((colorClass) => {
              const isSelected = colorClass === selectedColor;
              const swatchClasses = [
                colorClass,
                'w-11 h-11 rounded-md cursor-pointer',
                'transition-all duration-150',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                isSelected ? 'ring-2 ring-primary ring-offset-2' : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <button
                  key={colorClass}
                  type="button"
                  className={swatchClasses}
                  onClick={() => onColorChange(colorClass)}
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={EMOJI_COLOR_LABELS[colorClass] ?? colorClass}
                />
              );
            })}
          </div>
        </div>
      </div>
    );
  },
);

EmojiPicker.displayName = 'EmojiPicker';
